import { create } from 'zustand';

/**
 * FRONT-1991: the one signal that opens the desktop operator panel — the
 * overlay that shows a live view of the caller's registered machine plus
 * (once a task is running there) its step-by-step transcript.
 *
 * Mirrors `coding-open-store.ts`/`chat-open-store.ts`: a transient request
 * object rather than a boolean, so any surface (Settings' "Open desktop"
 * button today; a chat card once FRONT-1989 lands) can raise it without
 * knowing who renders the panel. `operator-panel-host.tsx` (mounted once in
 * `app-layout.tsx`) is the only reader that turns a request into a rendered
 * panel.
 *
 * `runId: null` means "just watch the desktop — no particular task", which
 * is what "Open desktop" raises. A non-null `runId` additionally opens the
 * transcript for that run (`operator-transcript.tsx`).
 *
 * Exported as plain functions (not just the hook) so a click handler outside
 * a component tree — or another store's effect enforcing the "one modal
 * viewer at a time" rule (CLAUDE.md) — can raise/clear the request without
 * needing to be a subscribed component itself. Same shape as
 * `dismissed-skill-runs-store.ts`'s exported `clear()`.
 *
 * `expanded` (FRONT-2185) is a separate field, deliberately outside
 * `OperatorPanelRequest` — it is the person's own "make this edge-to-edge"
 * choice for the panel's CHROME, not part of what request opened it.
 *
 * Review finding 9 (scope decision): `closeOperatorPanel` RESETS `expanded`
 * back to `false` — the agreed contract is that the control "toggles the
 * panel edge-to-edge and back", not "remembers edge-to-edge forever". A
 * panel a notification opens edge-to-edge, hours after someone last
 * expanded it and forgot, is a surprise nobody asked for. Never persisted
 * either way — a plain in-memory field, not `localStorage`.
 */

export interface OperatorPanelRequest {
  runId: string | null;
}

interface OperatorPanelState {
  request: OperatorPanelRequest | null;
  expanded: boolean;
  openOperatorPanel: (input?: { runId?: string }) => void;
  closeOperatorPanel: () => void;
  toggleOperatorPanelExpanded: () => void;
}

export const useOperatorPanelStore = create<OperatorPanelState>((set) => ({
  request: null,
  expanded: false,
  openOperatorPanel: (input) => set({ request: { runId: input?.runId ?? null } }),
  // Review finding 9: also resets `expanded` — see this file's own doc
  // comment above for why.
  closeOperatorPanel: () => set({ request: null, expanded: false }),
  toggleOperatorPanelExpanded: () => set((state) => ({ expanded: !state.expanded })),
}));

export function openOperatorPanel(input?: { runId?: string }): void {
  useOperatorPanelStore.getState().openOperatorPanel(input);
}

export function closeOperatorPanel(): void {
  useOperatorPanelStore.getState().closeOperatorPanel();
}

export function toggleOperatorPanelExpanded(): void {
  useOperatorPanelStore.getState().toggleOperatorPanelExpanded();
}
